import FW from '../fw/fw';
import Logger from '../fw/logger';


export default class AirPlay {

  #rmp;
  #airplayConnectTimeout = null;
  #onAirPlayCurrentPlaybackTargetIsWirelessChangedFn = null;
  #onMediaLoadedForcePlayOnLocalPlayerFn = null;
  #onAirPlayAvailabilityChangedFn = null;
  #onAirPlayClickFn = null;
  #airplayUIOn = false;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  #onAirPlayCurrentPlaybackTargetIsWirelessChanged(event) {
    if (event) {
      Logger.print('airplay', 'webkitcurrentplaybacktargetiswirelesschanged', event);
      if (this.#airplayUIOn) {
        this.#airplayUIOn = false;
        window.clearTimeout(this.#airplayConnectTimeout);
        this.#rmp.off('pause', this.#onMediaLoadedForcePlayOnLocalPlayerFn);
        this.#rmp.dom.airPlayIcon.classList.remove('rmp-i-airplay-connected');
        this.#rmp.dom.container.classList.remove('rmp-airplay-engaged-ui');
        this.#rmp.dispatch('airplaymediaunloaded');
      } else {
        this.#rmp.on('pause', this.#onMediaLoadedForcePlayOnLocalPlayerFn);
        this.#airplayConnectTimeout = window.setTimeout(() => {
          this.#rmp.off('pause', this.#onMediaLoadedForcePlayOnLocalPlayerFn);
        }, 10000);
        this.#airplayUIOn = true;
        this.#rmp.dom.airPlayIcon.classList.add('rmp-i-airplay-connected');
        this.#rmp.dom.container.classList.add('rmp-airplay-engaged-ui');
        this.#rmp.dispatch('airplaymedialoaded');
      }
    }
  }

  #onMediaLoadedForcePlayOnLocalPlayer() {
    this.#rmp.off('pause', this.#onMediaLoadedForcePlayOnLocalPlayerFn);
    this.#rmp.play().catch(error => {
      this.#rmp.rmpUtils.warning(1014, error);
    });
  }

  #onLoadedMetadataAirPlaySetup() {
    FW.show(this.#rmp.dom.airplay);
    // AirPlay with MMS does work with HLS
    if (this.#rmp.airplayWithMms && (this.#rmp.readingHlsJS || this.#rmp.readingHlsShaka)) {
      Logger.print('airplay', 'AirPlay with MMS and HLS');
      const source = document.createElement('source');
      source.type = 'application/x-mpegurl';
      source.src = this.#rmp.startingSrcUri;
      this.#rmp.dom.video.appendChild(source);
    }
    this.#rmp.dispatch('airplayavailable');
  }

  #setUpAirPlayUI() {
    // AirPlay with MMS does not work with MPEG-DASH - only HLS
    // see https://developer.apple.com/videos/play/wwdc2023/10122/ at 18:42
    if (this.#rmp.readingDashShaka) {
      Logger.print('airplay', 'MPEG-DASH AirPlay with MMS is not currently supported on Apple devices -> disable AirPlay');
      this.destroy();
      FW.removeElement(this.#rmp.dom.airplay);
      return;
    }
    if (!this.#rmp.dom.airplay) {
      this.#rmp.rmpModuleManagement.appendToModuleBar('airplay');
      this.#onAirPlayClickFn = this.onAirPlayClick.bind(this);
      FW.addEvents(['touchend', 'click'], this.#rmp.dom.airplay, this.#onAirPlayClickFn);
      this.#rmp.dom.container.classList.add('rmp-airplay-ui');
      this.#rmp.dom.airPlayIcon = this.#rmp.dom.container.querySelector('.rmp-i-airplay');
    }
    if (!this.#rmp.hasLoadedMetadata) {
      FW.hide(this.#rmp.dom.airplay);
      this.#rmp.one('loadedmetadata', () => {
        this.#onLoadedMetadataAirPlaySetup();
      });
    } else {
      this.#onLoadedMetadataAirPlaySetup();
    }
  }

  #onAirPlayAvailabilityChanged(event) {
    if (event) {
      switch (event.availability) {
        case 'available':
          Logger.print('airplay', 'AirPlay detected as available');
          this.#setUpAirPlayUI();
          break;
        case 'not-available':
          Logger.print('airplay', 'AirPlay detected as NOT available');
          this.#setUpAirPlayUI();
          break;
        default:
          FW.hide(this.#rmp.dom.airplay);
          break;
      }
    }
  }

  init() {
    if (typeof WebKitPlaybackTargetAvailabilityEvent !== 'undefined') {
      Logger.print('airplay', 'waiting for webkitplaybacktargetavailabilitychanged event');

      this.#onAirPlayAvailabilityChangedFn = this.#onAirPlayAvailabilityChanged.bind(this);
      this.#rmp.dom.video.addEventListener('webkitplaybacktargetavailabilitychanged',
        this.#onAirPlayAvailabilityChangedFn);

      this.#onMediaLoadedForcePlayOnLocalPlayerFn = this.#onMediaLoadedForcePlayOnLocalPlayer.bind(this);

      this.#onAirPlayCurrentPlaybackTargetIsWirelessChangedFn =
        this.#onAirPlayCurrentPlaybackTargetIsWirelessChanged.bind(this);
      this.#rmp.dom.video.addEventListener('webkitcurrentplaybacktargetiswirelesschanged',
        this.#onAirPlayCurrentPlaybackTargetIsWirelessChangedFn);
    }
  }

  onAirPlayClick(event) {
    FW.eventStopPrevent(event);
    Logger.print('airplay', 'webkitShowPlaybackTargetPicker request');
    try {
      this.#rmp.dom.video.webkitShowPlaybackTargetPicker();
    } catch (warning) {
      console.warn(warning);
    }
  }

  destroy() {
    window.clearTimeout(this.#airplayConnectTimeout);
    this.#rmp.dom.video.removeEventListener('webkitplaybacktargetavailabilitychanged',
      this.#onAirPlayAvailabilityChangedFn);
    this.#rmp.dom.video.removeEventListener('webkitcurrentplaybacktargetiswirelesschanged',
      this.#onAirPlayCurrentPlaybackTargetIsWirelessChangedFn);
  }
}
